/**
 * SlotRenderer — generic factory for shadcn compound components.
 *
 * Maps Cogo JSON type → shadcn React component 1:1.
 * No internal structure transformation. className pass-through. Children recursive.
 *
 * Usage:
 *   registry.register("card-header", createSlotRenderer(CardHeader))
 *   registry.register("empty-title", createSlotTextRenderer(EmptyTitle))
 */
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import { cn } from "../lib/utils"

/**
 * LLM-authored props that flow from Cogo JSON element → shadcn React component.
 * These mirror shadcn/HTML prop names so LLM-generated JSON matches shadcn examples.
 */
const PASS_THROUGH_PROPS = [
  "value", "defaultValue",
  "checked", "defaultChecked",
  "disabled", "readonly", "readOnly", "required",
  "variant", "size",
  "align", "orientation",
  "min", "max", "step",
  "name",
  "placeholder",
  "open", "defaultOpen",
  // Phase 5703 (v64.2): semantic heading tag for CardTitle / EmptyTitle / etc.
  // Maps to React `as` prop on shadcn title components that support polymorphism.
  "tag",
] as const

function extractProps(element: Record<string, unknown>): Record<string, unknown> {
  const props: Record<string, unknown> = {}
  for (const key of PASS_THROUGH_PROPS) {
    if (element[key] !== undefined) {
      // Normalize lowercase readonly → React readOnly
      const reactKey = key === "readonly" ? "readOnly" : key
      props[reactKey] = element[key]
    }
  }
  return props
}

/**
 * Container slot: renders Component with className + recursive children.
 * For structural components like CardHeader, CardContent, EmptyHeader, ItemContent, etc.
 *
 * NOTE: `Component` is typed as `React.ElementType` (not a strict `ComponentType<{...}>`)
 * so that strict base-ui wrappers (e.g. TabsTrigger with `TabsPrimitive.Tab.Props`,
 * RadioGroupItem with `RadioPrimitive.Root.Props`) can be passed through unchanged.
 * The shape contract (accepts className + children + pass-through props) is enforced
 * by convention at registration sites in `renderers/index.ts`.
 * (Plan 002-03 SIGNATURE fix for TS2345 sites 7-9.)
 */
export function createSlotRenderer(
  Component: React.ElementType,
  defaultProps?: Record<string, unknown>,
) {
  return function SlotRenderer({ element }: RendererProps) {
    const lifted = liftV4Props(element)
    const passProps = extractProps(lifted as unknown as Record<string, unknown>)
    return (
      <Component
        className={cn(lifted.className)}
        {...defaultProps}
        {...passProps}
      >
        {lifted.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </Component>
    )
  }
}

/**
 * Text slot: renders Component with className + resolved text content.
 * For text components like CardTitle, EmptyTitle, ItemTitle, AlertTitle, etc.
 *
 * See `createSlotRenderer` note above — same `React.ElementType` rationale.
 */
export function createSlotTextRenderer(
  Component: React.ElementType,
) {
  return function SlotTextRenderer({ element }: RendererProps) {
    const { resolveSymbol } = useRenderContext()
    const lifted = liftV4Props(element)
    const text = lifted.text ? String(resolveSymbol(lifted.text) ?? "") : ""
    const passProps = extractProps(lifted as unknown as Record<string, unknown>)

    return (
      <Component className={cn(lifted.className)} {...passProps}>
        {text}
        {lifted.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </Component>
    )
  }
}
